import { Link } from 'react-router-dom';
import { useHeroShowcase } from '../../hooks/useHeroShowcase';
import { safeImageUrl } from '../../utils/url';
import { StoreCatalogItem } from '../../models';
import { WHATSAPP_NUMBER, WHATSAPP_DISPLAY } from '../../constants/contact';
import { formatCurrency } from '../../utils/currency';
import { productPlaceholderIcon } from '../../utils/productPlaceholder';

const WHATSAPP_GROUP_URL = 'https://chat.whatsapp.com/J4ml8aLGIqmJGV2541LE0c?s=cl&p=i&mlu=4&amv=0';

function BellIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.75}
        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
      />
    </svg>
  );
}

function WhatsAppIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.75}
        d="M8 12a4 4 0 108 0 4 4 0 00-8 0zm9.5 5.5L21 21M4 12a8 8 0 1113.86 5.47L19 21l-3.66-1.14A8 8 0 014 12z"
      />
    </svg>
  );
}

function HeroShowcasePlaceholder({ pulse }: { pulse: boolean }) {
  return (
    <div
      className={`w-full h-full flex items-center justify-center bg-gradient-to-br from-champagne to-champagne-dark text-chocolate-dark/25 ${pulse ? 'animate-pulse' : ''}`}
    >
      <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M12 3a2 2 0 012 2c0 .35-.09.68-.24.97L18 9.5l3 1.5-1 2-5-1.5V19a1 1 0 01-1 1h-4a1 1 0 01-1-1v-7.5L4 13l-1-2 3-1.5 4.24-3.53A2 2 0 0110 5a2 2 0 012-2z"
        />
      </svg>
    </div>
  );
}

interface HeroShowcaseImageProps {
  currentProduct: StoreCatalogItem | null;
  currentIndex: number;
  total: number;
  hasMultiple: boolean;
  goToPrev: () => void;
  goToNext: () => void;
  isReady: boolean;
  isLoading: boolean;
  collectionName: string | null;
  collectionDescription: string | null;
}

function HeroShowcaseImage({
  currentProduct,
  currentIndex,
  total,
  hasMultiple,
  goToPrev,
  goToNext,
  isReady,
  isLoading,
  collectionName,
  collectionDescription,
}: HeroShowcaseImageProps) {
  if (!isReady || !currentProduct) {
    return <HeroShowcasePlaceholder pulse={isLoading} />;
  }

  const image = currentProduct.mainImageUrl || currentProduct.imageUrls?.[0];
  const displayPrice = currentProduct.promotionalPrice ?? currentProduct.price;
  const hasPromotion = !!currentProduct.promotionalPrice;
  const hasPrice = currentProduct.hasPrice && displayPrice !== null;

  const handlePrev = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    goToPrev();
  };

  const handleNext = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    goToNext();
  };

  return (
    <Link to={`/produtos/${currentProduct.id}`} className="group block w-full h-full">
      {image ? (
        <img
          src={safeImageUrl(image)}
          alt={currentProduct.name}
          draggable={false}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <img src={productPlaceholderIcon(currentProduct.id)} alt="" className="w-20 h-20 opacity-70" />
        </div>
      )}

      <div
        className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/75 via-black/25 to-transparent pointer-events-none"
        aria-hidden="true"
      />

      <div className="absolute inset-x-0 bottom-8 sm:bottom-9 px-4 sm:px-6 pointer-events-none">
        <h3 className="text-xl md:text-2xl font-display font-semibold text-white leading-tight drop-shadow-sm">
          {collectionName ?? 'Novidades da coleção'}
        </h3>
        {collectionDescription && (
          <p className="mt-1 text-sm text-champagne-light/90 line-clamp-2 max-w-[90%]">{collectionDescription}</p>
        )}
        <div className="mt-2 flex items-baseline gap-2">
          {hasPrice ? (
            <>
              {hasPromotion && (
                <span className="text-white/60 text-sm line-through [font-variant-numeric:tabular-nums]">
                  R$ {formatCurrency(currentProduct.price as number)}
                </span>
              )}
              <span className="font-bold text-white [font-variant-numeric:tabular-nums]">
                R$ {formatCurrency(displayPrice as number)}
              </span>
            </>
          ) : (
            <span className="text-sm font-medium text-white/80">Consulte-nos</span>
          )}
        </div>
      </div>

      {hasMultiple && (
        <>
          <button
            onClick={handlePrev}
            aria-label="Peça anterior"
            className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-text-dark flex items-center justify-center shadow-sm transition-opacity"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={handleNext}
            aria-label="Próxima peça"
            className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-text-dark flex items-center justify-center shadow-sm transition-opacity"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
            {Array.from({ length: total }).map((_, idx) => (
              <span
                key={idx}
                className={`h-1.5 rounded-full transition-all duration-200 ${
                  idx === currentIndex ? 'w-3.5 bg-white' : 'w-1.5 bg-white/55'
                }`}
              />
            ))}
          </div>
        </>
      )}
    </Link>
  );
}

export function Hero() {
  const {
    currentProduct,
    currentIndex,
    total,
    hasMultiple,
    goToPrev,
    goToNext,
    pauseAutoAdvance,
    resumeAutoAdvance,
    isReady,
    isLoading,
    collectionName,
    collectionDescription,
  } = useHeroShowcase();

  return (
    <section className="bg-transparent">
      <div className="max-w-6xl mx-auto px-4 py-10 md:py-14 grid md:grid-cols-[0.82fr_1fr] gap-10 md:gap-16 items-center">
        <div className="order-2 md:order-1">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-chocolate-dark mb-4">
            Moda infantil · Envio para todo o Brasil
          </p>
          <h1 className="font-brand text-3xl sm:text-4xl md:text-5xl text-text-dark leading-[1.05] tracking-tight mb-4">
            Vestimos criança
            <br />
            <span className="text-chocolate">como</span> criança.
          </h1>
          <p className="text-base md:text-lg text-text-medium leading-relaxed max-w-md mb-5">
            Roupas e acessórios do 1 aos 14 anos, para meninas e meninos.
          </p>
          <div className="w-11 h-0.5 bg-chocolate my-5" />
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <Link
              to="/catalogo?gender=2"
              className="inline-flex items-center gap-2 bg-white hover:bg-champagne-light text-chocolate-dark border border-chocolate-light font-semibold px-5 py-2.5 rounded-full transition-colors shadow-sm"
            >
              Meninas
            </Link>
            <Link
              to="/catalogo?gender=1"
              className="inline-flex items-center gap-2 bg-white hover:bg-olivine/10 text-olivine-dark border border-olivine font-semibold px-5 py-2.5 rounded-full transition-colors shadow-sm"
            >
              Meninos
            </Link>
            <Link
              to="/catalogo?sort=5"
              className="inline-flex items-center gap-2 bg-chocolate hover:bg-chocolate-dark text-white font-semibold px-5 py-2.5 rounded-full transition-colors shadow-sm"
            >
              Novidades
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-text-medium hover:text-chocolate font-semibold text-sm border-b border-champagne-dark hover:border-chocolate pb-0.5 transition-colors"
            >
              <WhatsAppIcon />
              Falar no WhatsApp
              <span className="text-text-light hidden sm:inline">· {WHATSAPP_DISPLAY}</span>
            </a>
            <a
              href={WHATSAPP_GROUP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-text-medium hover:text-chocolate font-semibold text-sm border-b border-champagne-dark hover:border-chocolate pb-0.5 transition-colors"
            >
              <BellIcon />
              Entrar no grupo de novidades
            </a>
          </div>
        </div>

        <div className="order-1 md:order-2 relative w-screen mx-[calc(50%-50vw)] md:w-auto md:mx-0">
          <div
            className="relative overflow-hidden shadow-none md:shadow-[0_25px_50px_-12px_rgba(60,36,21,0.25)] rounded-none md:rounded-[2rem] w-full md:w-auto aspect-[4/5] md:aspect-[3/4] max-h-[78vh] md:max-h-[calc(100vh-9rem)] bg-champagne-light"
            onMouseEnter={pauseAutoAdvance}
            onMouseLeave={resumeAutoAdvance}
          >
            <HeroShowcaseImage
              currentProduct={currentProduct}
              currentIndex={currentIndex}
              total={total}
              hasMultiple={hasMultiple}
              goToPrev={goToPrev}
              goToNext={goToNext}
              isReady={isReady}
              isLoading={isLoading}
              collectionName={collectionName}
              collectionDescription={collectionDescription}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
